import React, { useState, useEffect, useMemo } from 'react';
import './AILoadingAnimation.css';
import { dolphinJokes } from '../data/dolphinJokes';

// ── Per-group configuration ────────────────────────────────────────────────────
const GROUP_CONFIG = {
  Dolfijnen: {
    emoji:      '🐬',
    accent:     '#00dcff',
    bg:         'linear-gradient(160deg, #050c28 0%, #08163d 45%, #060e2a 100%)',
    glow:       'rgba(0, 80, 200, 0.18)',
    wave1:      'rgba(0, 55, 140, 0.50)',
    wave2:      'rgba(0, 70, 165, 0.35)',
    wave3:      'rgba(0, 90, 190, 0.25)',
    bubbleColor:'rgba(80, 190, 255, 0.18)',
    bubbleBorder:'rgba(100, 210, 255, 0.35)',
    steps: [
      { icon: '📸', text: 'Afbeelding laden...' },
      { icon: '🔍', text: 'Tekst herkennen...' },
      { icon: '🧠', text: 'AI analyseren...' },
      { icon: '💰', text: 'Bedrag berekenen...' },
      { icon: '📅', text: 'Datum verifiëren...' },
      { icon: '✨', text: 'Gegevens extraheren...' },
    ],
  },

  Zeeverkenners: {
    emoji:      '⛵',
    accent:     '#f0c040',
    bg:         'linear-gradient(160deg, #040d1a 0%, #071525 45%, #050e1c 100%)',
    glow:       'rgba(200, 150, 0, 0.12)',
    wave1:      'rgba(0, 40, 90, 0.55)',
    wave2:      'rgba(0, 55, 110, 0.38)',
    wave3:      'rgba(0, 70, 130, 0.28)',
    bubbleColor:'rgba(240, 210, 80, 0.12)',
    bubbleBorder:'rgba(240, 200, 60, 0.30)',
    steps: [
      { icon: '🗺️', text: 'Kaart raadplegen...' },
      { icon: '⚓', text: 'Anker ophalen...' },
      { icon: '🔭', text: 'Horizon aftasten...' },
      { icon: '💰', text: 'Bedrag noteren...' },
      { icon: '📅', text: 'Datum in logboek...' },
      { icon: '✨', text: 'Gegevens extraheren...' },
    ],
  },

  Wildevaart: {
    emoji:      '🚣',
    accent:     '#2de8a0',
    bg:         'linear-gradient(160deg, #03100c 0%, #061a12 45%, #040e0a 100%)',
    glow:       'rgba(0, 180, 100, 0.14)',
    wave1:      'rgba(0, 80, 50, 0.55)',
    wave2:      'rgba(0, 100, 60, 0.38)',
    wave3:      'rgba(0, 120, 70, 0.28)',
    bubbleColor:'rgba(40, 220, 140, 0.14)',
    bubbleBorder:'rgba(50, 230, 150, 0.32)',
    steps: [
      { icon: '🌊', text: 'Stroming meten...' },
      { icon: '🚣', text: 'Koers bepalen...' },
      { icon: '🗺️', text: 'Route in kaart...' },
      { icon: '💰', text: 'Bedrag berekenen...' },
      { icon: '📅', text: 'Datum vastleggen...' },
      { icon: '✨', text: 'Gegevens extraheren...' },
    ],
  },
};

const AMOUNT_FRAMES = ['€ --,--', '€ ??,??', '€ ##,##', '€ **,**'];

function AILoadingAnimation({ activeGroup }) {
  const config = GROUP_CONFIG[activeGroup] || GROUP_CONFIG.Dolfijnen;

  const [joke, setJoke] = useState('');
  const [stepIndex, setStepIndex] = useState(0);
  const [jokeVisible, setJokeVisible] = useState(false);
  const [amountFrame, setAmountFrame] = useState(0);

  const bubbles = useMemo(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      left:     4 + (i * 7.1) % 92,
      size:     4 + (i * 3)   % 10,
      duration: 3.5 + (i * 0.6) % 4,
      delay:    (i * 0.55) % 5.5,
    })), []);

  const docLines = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => ({
      id: i,
      width: 42 + (i * 11) % 44,
      delay: i * 0.13,
    })), []);

  useEffect(() => {
    setJoke(dolphinJokes[Math.floor(Math.random() * dolphinJokes.length)]);
    setTimeout(() => setJokeVisible(true), 700);
  }, []);

  useEffect(() => {
    const s = setInterval(() =>
      setStepIndex(i => (i + 1) % config.steps.length), 1800);
    return () => clearInterval(s);
  }, [config.steps.length]);

  useEffect(() => {
    const a = setInterval(() =>
      setAmountFrame(i => (i + 1) % AMOUNT_FRAMES.length), 380);
    return () => clearInterval(a);
  }, []);

  // CSS custom properties injected inline so a single stylesheet drives everything
  const cssVars = {
    '--accent':        config.accent,
    '--glow':          config.glow,
    '--wave1':         config.wave1,
    '--wave2':         config.wave2,
    '--wave3':         config.wave3,
    '--bubble-bg':     config.bubbleColor,
    '--bubble-border': config.bubbleBorder,
    background:        config.bg,
  };

  // Wildevaart waves are faster / more turbulent
  const waveClass = activeGroup === 'Wildevaart' ? 'ocean rapids' : 'ocean';

  return (
    <div className="ai-loading-overlay" style={cssVars}>

      {/* Floating background elements */}
      {bubbles.map(b => (
        <div key={b.id} className="bubble" style={{
          left: `${b.left}%`,
          width: `${b.size}px`,
          height: `${b.size}px`,
          animationDuration: `${b.duration}s`,
          animationDelay: `${b.delay}s`,
        }} />
      ))}

      <div className="ai-loading-container">

        {/* Scene */}
        <div className="scene">
          <div className="dolphin-x">
            <div className={`dolphin-y ${activeGroup === 'Zeeverkenners' ? 'sailboat' : ''}`}>
              {config.emoji}
            </div>
          </div>

          <div className="receipt-doc">
            <div className="receipt-corner tl" />
            <div className="receipt-corner tr" />
            <div className="receipt-corner bl" />
            <div className="receipt-corner br" />
            <div className="scan-line" />
            <div className="doc-lines">
              {docLines.map(l => (
                <div key={l.id} className="doc-line" style={{
                  width: `${l.width}%`,
                  animationDelay: `${l.delay}s`,
                }} />
              ))}
            </div>
            <div className="doc-amount">{AMOUNT_FRAMES[amountFrame]}</div>
          </div>
        </div>

        {/* Step pill — keyed so it bounces on each change */}
        <div key={stepIndex} className="step-pill">
          <span className="step-pill-icon">{config.steps[stepIndex].icon}</span>
          <span className="step-pill-text">{config.steps[stepIndex].text}</span>
        </div>

        {/* Progress dots */}
        <div className="step-dots">
          {config.steps.map((_, i) => (
            <div key={i} className={`step-dot${i === stepIndex ? ' active' : i < stepIndex ? ' done' : ''}`} />
          ))}
        </div>

        {/* Joke */}
        {jokeVisible && (
          <div className="joke-card">
            <div className="joke-label">
              {activeGroup === 'Zeeverkenners' ? '⚓ Zeemansmop' :
               activeGroup === 'Wildevaart'    ? '🌊 Wildwatermop' :
                                                 '🌊 Waterscouting grapje'}
            </div>
            <p className="joke-text">{joke}</p>
          </div>
        )}
      </div>

      {/* Ocean / rapids */}
      <div className={waveClass}>
        <div className="wave wave1" />
        <div className="wave wave2" />
        <div className="wave wave3" />
      </div>
    </div>
  );
}

export default AILoadingAnimation;
